import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { SortedField } from '~/shared/data-contract/common';

import { useTablePagination } from '../../../common/data-grids/common/use-table-pagination';
import type { EmployeeRow } from '../../../common/data-grids/employees/EmployeesDataGrid';
import { EmployeesDataGrid } from '../../../common/data-grids/employees/EmployeesDataGrid';
import { useEmployeesColumns } from '../../../common/data-grids/employees/use-employees-columns';
import { useNavigationToEntity } from '../../../common/use-navigation-to-entity';
import { defaultPagination } from '../../../global-search/use-global-search-table-params';
import { useGlobalSearchUsersRelatedSkillsQuery } from '../../../query-hooks/global-search/users';
import type { SortOption } from '../../talent-directory/sort-option';

const sortItemToEmployeeSortItems = (item: SortOption): readonly SortedField[] =>
    item.field === 'employeeName'
        ? [
              {
                  direction: item.direction ?? 'asc',
                  field: 'firstName',
              },
              {
                  direction: item.direction ?? 'asc',
                  field: 'lastName',
              },
          ]
        : [item];

type EmployeeRelatedSkillsTableProps = {
    readonly skillIds: ReadonlyArray<string>;
};

export const EmployeeRelatedSkillsTable: FunctionComponent<EmployeeRelatedSkillsTableProps> = ({ skillIds }) => {
    const { translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();
    const { navigateToUserProfile } = useNavigationToEntity();

    const [sortOptions, setSortOptions] = useState<readonly SortOption[]>([]);

    const { data, isFetching, isPending } = useGlobalSearchUsersRelatedSkillsQuery({
        pagination: { page: paginationModel.page, size: paginationModel.pageSize },
        skillIds,
        sortedFields: sortOptions.flatMap(sortItemToEmployeeSortItems),
    });

    const columns = useEmployeesColumns(skillIds);

    const users = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;

    const noDataMessage = translate('pages.global-search.no-data.employees');

    const rows: readonly EmployeeRow[] = users.map((user) => ({
        id: user.id,
        emailAddress: user.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        skills: user.skills,
    }));

    return (
        <div style={{ height: '70vh', width: '100%' }}>
            <EmployeesDataGrid
                columns={columns}
                isFetching={isFetching}
                isPending={isPending}
                noDataMessage={noDataMessage}
                paginationModel={paginationModel}
                rowCount={pagination.count}
                rows={rows}
                sortOptions={sortOptions}
                onPaginationModelChange={setPaginationModel}
                onRowClick={navigateToUserProfile}
                onSortOptionsChange={setSortOptions}
            />
        </div>
    );
};
